import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { BeefXDairyData } from "generated/graphql";

type BeefXDairyTableData = Omit<BeefXDairyData, "createdAt" | "updatedAt">;
interface BeefXDairyDataTableProps {
  beefXDairyData: BeefXDairyTableData | null | undefined;
}

export const BeefXDairyDataTable: React.FC<BeefXDairyDataTableProps> = ({ beefXDairyData }) => {
  const { sampleId, barcode, adg, marb, hcw, terminalIndex, dairyPct, beefPct, primaryDairyBreed, primaryBeefBreed } =
    beefXDairyData || {};

  const data: BeefXDairyTableData[] = beefXDairyData
    ? [
        {
          sampleId,
          barcode,
          adg,
          marb,
          hcw,
          terminalIndex,
          dairyPct: dairyPct || 0,
          beefPct: beefPct || 0,
          primaryDairyBreed,
          primaryBeefBreed,
        },
      ]
    : [];

  const columnHelper = createColumnHelper<BeefXDairyTableData>();
  const columns = [
    columnHelper.accessor("sampleId", {
      id: "sampleId",
      header: () => <span>Sample Id</span>,
      cell: (info) => info.getValue() || "-",
    }),
    columnHelper.accessor((row) => row.barcode, {
      id: "barcode",
      header: () => <span>Barcode</span>,
      cell: (info) => info.getValue() || "-",
    }),
    columnHelper.accessor((row) => row.adg, {
      id: "adg",
      header: () => <span>ADG</span>,
      cell: (info) => info.getValue() || "-",
      size: 10,
    }),
    columnHelper.accessor((row) => row.marb, {
      id: "marb",
      header: () => <span>MARB</span>,
      cell: (info) => info.getValue() || "-",
    }),
    columnHelper.accessor((row) => row.hcw, {
      id: "hcw",
      header: () => <span>HCW</span>,
      cell: (info) => info.getValue() || "-",
    }),
    columnHelper.accessor((row) => row.terminalIndex, {
      id: "terminalIndex",
      header: () => <span>Terminal Index</span>,
      cell: (info) => info.getValue() || "-",
    }),
    columnHelper.accessor((row) => row.dairyPct, {
      id: "dairyPct",
      header: () => <span> % Dairy </span>,
      cell: (info) => `${info.getValue()}%` || "-",
    }),
    columnHelper.accessor((row) => row.beefPct, {
      id: "beefPct",
      header: () => <span>% Beef</span>,
      cell: (info) => `${info.getValue()}%` || "-",
    }),
    columnHelper.accessor((row) => row.primaryDairyBreed, {
      id: "primaryDairyBreed",
      header: () => <span>Primary Dairy Breed</span>,
      cell: (info) => info.getValue() || "-",
    }),
    columnHelper.accessor((row) => row.primaryBeefBreed, {
      id: "primaryBeefBreed",
      header: () => <span>Primary Beef Breed</span>,
      cell: (info) => info.getValue() || "-",
    }),
  ];

  const table = useReactTable({
    data: data,
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <>
      {data.length > 0 ? (
        <>
          {/*TODO create and move to genetic table component*/}
          <table className="w-full table-fixed shadow overflow-hidden rounded-lg">
            <thead className="">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      style={{ borderRight: "1px solid #fff" }}
                      className="h-8 w-full p-2 bg-breedr-blue-500 text-white text-left text-xs uppercase "
                      key={header.id}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td
                      style={{ border: "1px solid #f3f5f7", width: cell.column.getSize() }}
                      className="px-4 bg-white text-xs leading-8 text-ellipsis overflow-hidden"
                      key={cell.id}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : (
        <p>Upload Beef x Dairy data for this animal to view it here.</p>
      )}
    </>
  );
};
